/* IntelApps — Portfolio Section */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { R_PROJECTS } from './Hero.js';
import { useI18n } from '../i18n.js';

const PROJECTS = [
  {
    id: 'doordash',
    title: 'Doordash — Food Delivery App',
    meta: 'Mobile · iOS + Android',
    img: R_PROJECTS.doordash,
    tag: 'Mobile',
    result: '↑ 4.7★ App Store',
  },
  {
    id: 'ankerkrut',
    title: 'Ankerkrut - Kitchen & Bath Appliances',
    meta: 'Web · Kitchen & Bath Appliances',
    img: R_PROJECTS.ankerkrut,
    tag: 'Web',
    result: '120K+ products',
  },
  {
    id: 'oanda',
    title: 'Oanda - Forex Trading Platform',
    meta: 'Mobile · Forex Trading',
    img: R_PROJECTS.oanda,
    tag: 'Mobile',
    result: '100% uptime',
  },
  {
    id: 'shein',
    title: 'Shein - Fashion App',
    meta: 'Mobile · Fashion',
    img: R_PROJECTS.shein,
    tag: 'Mobile',
    result: '4.8★ avg. across stores',
  },
  {
    id: 'staymenity',
    title: 'Staymenity',
    meta: 'SaaS · Property tech',
    img: R_PROJECTS.amenity,
    tag: 'Web',
    result: '40% cost reduction',
  },
  {
    id: 'studio',
    title: 'Studio AI',
    meta: 'AI · Photography',
    img: R_PROJECTS.studioai,
    tag: 'AI',
    result: '100% uptime',
  },
  {
    id: 'promod',
    title: 'Promod',
    meta: 'Web · E-commerce',
    img: R_PROJECTS.promod,
    tag: 'Web',
    result: '120K+ products',
  },
  
  {
    id: 'noble',
    title: 'Nobleblocks',
    meta: 'DApp · Scientific publishing',
    img: R_PROJECTS.scifi,
    tag: 'Blockchain',
    result: '1,200 papers minted',
  },
  {
    id: 'winify',
    title: 'Winify AI Agent',
    meta: 'AI · RFP automation',
    img: R_PROJECTS.aiagent,
    tag: 'AI',
    result: '96% time saved',
  },
  {
    id: 'rollei',
    title: 'Rollei',
    meta: 'Web · Photography',
    img: R_PROJECTS.rollei,
    tag: 'Web',
    result: '100% uptime',
  },
  {
    id: 'easemate',
    title: 'Easemate',
    meta: 'AI · Image generation',
    img: R_PROJECTS.easemate,
    tag: 'Web',
    result: '100% uptime',
  },
];

const TESTIMONIALS = [
  {
    quote: "IntelApps shipped our AI platform 3 months ahead of schedule. The team's work on retrieval and evals was the difference between a demo and a product.",
    name: 'Sasha Rose',
    role: 'Founder, Surveyor Corps',
    img: '/assets/testimonials/sasha.jpg',
    project: 'AI Customer Support Platform',
    timeline: '10 wks (vs. 16 estimated)',
    result: '–60% support cost',
  },
  {
    quote: "They handled our smart contracts and the consumer DApp with the same level of care. Mainnet launch was the calmest ship day I've ever had.",
    name: 'Kruger Khan',
    role: 'Director, Shultan Oil',
    img: '/assets/testimonials/kruger.jpg',
    project: 'Energy Settlements DApp',
    timeline: '14 weeks',
    result: '$2.1M settled in week one',
  },
  {
    quote: "Most agencies feel like outsourcing. IntelApps felt like senior teammates who already knew our roadmap.",
    name: 'Reiner John',
    role: 'CEO, Marley Co',
    img: '/assets/testimonials/reiner.jpg',
    project: 'Marley iOS + Android app',
    timeline: '12 weeks',
    result: '4.8★ avg. across stores',
  },
];

export default function PortfolioSection({
  showFeatured = true,
  showFilters = true,
  showTeaser = false,
}) {
  const { t } = useI18n();
  const [filter, setFilter] = useState('all');
  const [testiIdx, setTestiIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTestiIdx((i) => (i + 1) % TESTIMONIALS.length), 7000);
    return () => clearInterval(id);
  }, []);

  const filterKeys = ['all', 'ai', 'mobile', 'web', 'blockchain'];
  const tagByKey = {
    ai: 'AI',
    mobile: 'Mobile',
    web: 'Web',
    blockchain: 'Blockchain',
  };
  const visible = filter === 'all'
    ? PROJECTS
    : PROJECTS.filter((p) => p.tag === tagByKey[filter]);
  const teaserProjects = showTeaser ? PROJECTS.slice(0, 3) : visible;

  return (
    <>
      <section className="section" id="work">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow">{t.portfolio.eyebrow}</span>
              <h2>{t.portfolio.title}</h2>
            </div>
            <p>{t.portfolio.description}</p>
          </div>
          {showFilters && !showTeaser && (
            <div className="portfolio-filters">
              {filterKeys.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`filter ${filter === k ? 'active' : ''}`}
                  onClick={() => setFilter(k)}
                >
                  {t.portfolio.filters[k]}
                </button>
              ))}
            </div>
          )}
          <div className="portfolio-grid">
            {showFeatured && !showTeaser && (
              <div className="case case-featured">
                <div className="case-img">
                  <img src={R_PROJECTS.aiagent} alt="Winify" />
                </div>
                <div className="case-featured-body">
                  <div>
                    <span className="hero-card-tag" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      {t.portfolio.caseStudy.tag}
                    </span>
                    <h3 style={{ marginTop: 10 }}>{t.portfolio.caseStudy.title}</h3>
                    <p className="case-meta">{t.portfolio.caseStudy.meta}</p>
                    <blockquote className="case-featured-quote">
                      &ldquo;{t.portfolio.caseStudy.quote}&rdquo;
                    </blockquote>
                  </div>
                  <div className="case-featured-stats">
                    <div className="case-featured-stat">
                      <div className="num">{t.portfolio.caseStudy.stats.timeSaved.num}</div>
                      <div className="lbl">{t.portfolio.caseStudy.stats.timeSaved.lbl}</div>
                    </div>
                    <div className="case-featured-stat">
                      <div className="num">{t.portfolio.caseStudy.stats.winRate.num}</div>
                      <div className="lbl">{t.portfolio.caseStudy.stats.winRate.lbl}</div>
                    </div>
                    <div className="case-featured-stat">
                      <div className="num">{t.portfolio.caseStudy.stats.pipeline.num}</div>
                      <div className="lbl">{t.portfolio.caseStudy.stats.pipeline.lbl}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {teaserProjects.filter((p) => !showFeatured || p.id !== 'winify').map((p) => (
              <div key={p.id} className="case span-2">
                <div className="case-img">
                  <span className="case-img-tag">{p.tag}</span>
                  <img src={p.img} alt={p.title} />
                </div>
                <div className="case-body">
                  <h4 className="case-title">{p.title}</h4>
                  <p className="case-meta">{p.meta}</p>
                  {p.result && <span className="case-result">{p.result}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {showTeaser && (
        <section className="section">
          <div className="container">
            <div className="section-head">
              <div>
                <span className="eyebrow">{t.portfolio.clients.eyebrow}</span>
                <h2>{t.portfolio.clients.title}</h2>
              </div>
            </div>
            <div className="testi">
              <div>
                <p className="testi-quote">
                  <span className="accent">&ldquo;</span>
                  {TESTIMONIALS[testiIdx].quote}
                  <span className="accent">&rdquo;</span>
                </p>
                <div className="testi-person">
                  <img src={TESTIMONIALS[testiIdx].img} alt={TESTIMONIALS[testiIdx].name} />
                  <div>
                    <div className="testi-person-name">{TESTIMONIALS[testiIdx].name}</div>
                    <div className="testi-person-role">{TESTIMONIALS[testiIdx].role}</div>
                  </div>
                </div>
                <div className="testi-nav">
                  {TESTIMONIALS.map((tst, idx) => (
                    <button
                      key={tst.name}
                      type="button"
                      className={`testi-dot ${idx === testiIdx ? 'active' : ''}`}
                      onClick={() => setTestiIdx(idx)}
                    />
                  ))}
                </div>
              </div>
              <div className="testi-card">
                <div className="testi-card-row">
                  <span className="lbl">{t.testimonials.project}</span>
                  <span className="val">{TESTIMONIALS[testiIdx].project}</span>
                </div>
                <div className="testi-card-row">
                  <span className="lbl">{t.testimonials.timeline}</span>
                  <span className="val">{TESTIMONIALS[testiIdx].timeline}</span>
                </div>
                <div className="testi-card-row">
                  <span className="lbl">{t.testimonials.outcome}</span>
                  <span className="val green">{TESTIMONIALS[testiIdx].result}</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

PortfolioSection.propTypes = {
  // eslint-disable-next-line react/require-default-props
  showFeatured: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  showFilters: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  showTeaser: PropTypes.bool,
};

export { TESTIMONIALS, PROJECTS };
